import {
  Component,
  ElementRef,
  inject,
  OnInit,
  OnDestroy,
  signal,
  ViewChild,
} from "@angular/core";
import {
  FormControl,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";

import { ProductService } from "../services/product.service";
import { OrderService } from "../services/order.service";
import { Product } from "../models/product";
import { AnswerOption } from "../models/answeroption";
import { TextService } from "../services/text.service";
import { TranslateService } from "@ngx-translate/core";
import { is_filled_in } from "../misc/functions";
import { ProductSubCategory } from "../models/productsubcategory";
import { AsyncPipe, CommonModule } from "@angular/common";
import { TtcurrencyPipe } from "../pipes/ttcurrency.pipe";
import { CartStore } from "../store/cart.store";
import { faBurger } from "@fortawesome/free-solid-svg-icons";
import {
  AlertComponent as TtAlertComponent,
  ButtonComponent as TtButtonComponent,
  ModalComponent as TtModalComponent,
  OptionGroupComponent as TtOptionGroupComponent,
} from "@ticket-team-com/tt-component-library";

@Component({
  selector: "app-ticket-overview",
  templateUrl: "./products.component.html",
  styleUrls: ["./products.component.css"],
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    TtcurrencyPipe,
    CommonModule,
    TtAlertComponent,
    TtButtonComponent,
    TtModalComponent,
    TtOptionGroupComponent,
  ],
})
export class ProductsComponent implements OnInit, OnDestroy {
  public products: ProductSubCategory[] = [];
  form: UntypedFormGroup;

  public showOptionModal = false;
  public currentProductId: string;

  public tableError = false;
  public image: string;
  public showImageModal = false;

  public activeCategoryId = signal<string | null>(null);
  private observer: IntersectionObserver | null = null;

  @ViewChild("cartButton", { read: ElementRef })
  private cartButton?: ElementRef<HTMLElement>;

  private route = inject(ActivatedRoute);
  private formBuilder = inject(UntypedFormBuilder);
  public textService = inject(TextService);
  private router = inject(Router);
  private productsService = inject(ProductService);
  private translateService = inject(TranslateService);
  private orderService = inject(OrderService);
  public cartStore = inject(CartStore);

  ngOnInit() {
    this.form = new UntypedFormGroup({});

    this.route.queryParams.subscribe((params) => {
      const tableId = params.tableId;

      if (is_filled_in(tableId)) {
        this.orderService.setTableId(tableId);
      }

      if (is_filled_in(this.orderService.getTableId())) {
        this.getProducts();
        this.tableError = false;
      } else {
        this.tableError = true;
      }
    });

    this.translateService.onDefaultLangChange.subscribe(() => {
      this.getProducts();
    });

    this.orderService.removeExtraCosts();
  }

  previewImage(image: string) {
    this.image = image;
    this.showImageModal = true;
  }

  // Build the tile options for `tt-option-group`, cheapest first so the free
  // option lands as the default selection. The component renders the squircle
  // tiles, selection state and `+price` formatting itself.
  getAnswerOptions(
    answers: AnswerOption[],
  ): { label: string; value: string; price?: number }[] {
    return [...answers]
      .sort((a, b) => (a.price ?? 0) - (b.price ?? 0))
      .map((a) => ({ label: a.answer, value: a.id, price: a.price }));
  }

  private createOptionsFormGroup(product: Product) {
    const items = {};

    for (const question of product.questions) {
      const firstFreeItem = question.answers.find((item) => {
        return item.price == null;
      });
      items[question.id] = new FormControl(firstFreeItem.id);
    }
    return this.formBuilder.group(items, { validators: Validators.required });
  }

  private createProductFormGroup(product: Product) {
    return this.formBuilder.group({
      quantity: [
        {
          value:
            this.cartStore
              .articles()
              .find((cartItems) => cartItems.product.id === product.id)
              ?.quantity || "0",
          disabled: true,
        },
      ],
      options: this.createOptionsFormGroup(product),
    });
  }

  getProducts() {
    this.productsService.getAllCategoriesAndProducts().subscribe((products) => {
      this.products = products;

      if (!this.products.length) {
        this.tableError = true;
        return;
      }

      const groups = {};

      for (const subCategory of this.products) {
        for (const product of subCategory.products) {
          groups[product.id] = this.createProductFormGroup(product);
        }
      }

      this.form = this.formBuilder.group(groups);
      this.initObserver();
    });
  }

  shouldEnableActionButtons() {
    return this.cartStore.articles().length > 0;
  }

  confirmOrder() {
    this.router.navigateByUrl("/payment");
  }

  getProductQuantity(productId: string): number {
    return this.cartStore
      .articles()
      .filter((a) => a.product.id === productId)
      .reduce((sum, a) => sum + a.quantity, 0);
  }

  decrement(id: string) {
    if (this.getProductQuantity(id) === 0) {
      return;
    }
    this.orderService.removeArticleByProductId(id);
  }

  addProductWithQuestions(id: string) {
    const currentProduct = this.productsService.getProductById(
      id,
      this.products,
    );

    this.currentProductId = id;

    const answers = this.form.get(id).get("options").value;

    const questionDetails = {};
    for (const questionId in answers) {
      // eslint-disable-next-line no-prototype-builtins
      if (!answers.hasOwnProperty(questionId)) {
        continue;
      }
      const answerId = answers[questionId];
      questionDetails[questionId] = this.productsService.getQuestionDetails(
        currentProduct,
        questionId,
        answerId,
      );
    }

    this.orderService.addProduct(currentProduct, questionDetails);
    this.flyImageToCart(id);
  }

  increment(id: string) {
    const currentProduct = this.productsService.getProductById(
      id,
      this.products,
    );

    if (currentProduct.questions.length == 0) {
      this.currentProductId = id;
      this.orderService.addProduct(currentProduct);
      this.flyImageToCart(id);
      this.showOptionModal = false;
      return;
    }

    const isReopening = this.showOptionModal && this.currentProductId === id;
    this.currentProductId = id;

    if (isReopening && this.form.get(id).get("options").valid) {
      this.addProductWithQuestions(id);
    }

    this.showOptionModal = true;
  }

  /**
   * Visual feedback when an item is added: the product image (or a burger icon
   * when the product has no image) floats in a small arc towards the fixed
   * checkout button, which then pulses (grows briefly and settles back) as the
   * item "lands".
   */
  private flyImageToCart(productId: string) {
    const hostEl = this.cartButton?.nativeElement;
    if (!hostEl) {
      return;
    }

    // tt-button's host element is display:inline, so transforms on it don't
    // render — animate the inner <button>/<a> that actually paints instead.
    const buttonEl = hostEl.querySelector<HTMLElement>("button, a") ?? hostEl;

    const pulse = () =>
      buttonEl.animate(
        [
          { transform: "scale(1)" },
          { transform: "scale(1.08)", offset: 0.45 },
          { transform: "scale(1)" },
        ],
        { duration: 500, easing: "ease-out" },
      );

    const prefersReducedMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (prefersReducedMotion) {
      pulse();
      return;
    }

    // Build the node to fly and the rect it should start from: the product
    // image when present, otherwise a burger icon anchored at the product row.
    const sourceImg = document.getElementById(
      "product-img-" + productId,
    ) as HTMLImageElement | null;

    let flyer: HTMLElement;
    let start: DOMRect | { left: number; top: number; width: number; height: number };

    if (sourceImg) {
      flyer = sourceImg.cloneNode(true) as HTMLImageElement;
      start = sourceImg.getBoundingClientRect();
    } else {
      const row = document.getElementById("product-row-" + productId);
      if (!row) {
        pulse();
        return;
      }
      const size = 40;
      const rowRect = row.getBoundingClientRect();
      // Mimic where the image would sit: left padding, vertically centred.
      start = {
        left: rowRect.left + 20,
        top: rowRect.top + rowRect.height / 2 - size / 2,
        width: size,
        height: size,
      };
      flyer = this.createBurgerNode();
    }

    Object.assign(flyer.style, {
      position: "fixed",
      left: `${start.left}px`,
      top: `${start.top}px`,
      width: `${start.width}px`,
      height: `${start.height}px`,
      margin: "0",
      zIndex: "50",
      pointerEvents: "none",
      borderRadius: "0.5rem",
    });
    document.body.appendChild(flyer);

    const end = buttonEl.getBoundingClientRect();
    const dx = end.left + end.width / 2 - (start.left + start.width / 2);
    const dy = end.top + end.height / 2 - (start.top + start.height / 2);

    // Lift the midpoint above the straight line so the path bows into an arc.
    const arcLift = Math.min(140, Math.abs(dy) * 0.4 + 60);

    const animation = flyer.animate(
      [
        { transform: "translate(0, 0) scale(1)", opacity: 1 },
        {
          transform: `translate(${dx / 2}px, ${dy / 2 - arcLift}px) scale(0.6)`,
          opacity: 0.9,
          offset: 0.5,
        },
        {
          transform: `translate(${dx}px, ${dy}px) scale(0.15)`,
          opacity: 0.2,
        },
      ],
      { duration: 650, easing: "cubic-bezier(0.4, 0, 0.6, 1)" },
    );

    animation.onfinish = () => {
      flyer.remove();
      pulse();
    };
  }

  /** Builds a standalone SVG burger icon element to fly towards the cart. */
  private createBurgerNode(): HTMLElement {
    const svgNs = "http://www.w3.org/2000/svg";
    const [width, height, , , pathData] = faBurger.icon;
    const svg = document.createElementNS(svgNs, "svg");
    svg.setAttribute("viewBox", `0 0 ${width} ${height}`);
    svg.style.width = "100%";
    svg.style.height = "100%";
    svg.style.color = "#2563eb";
    svg.style.filter = "drop-shadow(0 2px 3px rgba(0,0,0,0.2))";

    const path = document.createElementNS(svgNs, "path");
    path.setAttribute("d", Array.isArray(pathData) ? pathData.join(" ") : pathData);
    path.setAttribute("fill", "currentColor");
    svg.appendChild(path);

    return svg as unknown as HTMLElement;
  }

  scrolltoElement(id: string) {
    const el = document.getElementById(id);
    if (!el) return;
    const stickyBar = document.querySelector<HTMLElement>(".sticky.top-0");
    const offset = stickyBar ? stickyBar.offsetHeight : 0;
    const top = el.getBoundingClientRect().top + window.scrollY - offset - 8;
    window.scrollTo({ top, behavior: "smooth" });
  }

  private initObserver() {
    this.observer?.disconnect();

    // sticky category bar is ~56px
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.activeCategoryId.set(entry.target.id);
            break;
          }
        }
      },
      { rootMargin: "-56px 0px -60% 0px", threshold: 0 },
    );

    setTimeout(() => {
      for (const category of this.products) {
        const el = document.getElementById(category.id);
        if (el) this.observer!.observe(el);
      }
    });
  }

  ngOnDestroy() {
    this.observer?.disconnect();
  }
}
